"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CostaClient = void 0;
const axios_1 = require("axios");
const Errors_1 = require("./types/Errors");
const RiseupAccount_1 = require("./types/RiseupAccount");
const RiseupAccountRequests_1 = require("./types/RiseupAccountRequests");
class CostaClient {
    constructor(costaUrl, logger) {
        const defaultTransformer = axios_1.default.defaults.transformResponse;
        this.logger = logger;
        this.costaClient = axios_1.default.create({
            baseURL: costaUrl,
            headers: {
                'content-type': 'application/json',
                Accept: 'application/json',
            },
            validateStatus(status) {
                return status >= 200 && status < 300;
            },
            transformResponse: defaultTransformer.concat([data => {
                    if (data === '') {
                        return null;
                    }
                    return data;
                }]),
        });
    }
    async createMember(memberCreateRequest) {
        this.logger.info('Calling costa with member create request', { memberCreateRequest });
        const costaCreateResponse = await this.costaClient.post('/api/members', memberCreateRequest)
            .catch(e => {
            if (e.response.data === new Errors_1.MemberExistsError().message) {
                throw new Errors_1.MemberExistsError(memberCreateRequest);
            }
            if (e.response.data === new Errors_1.MemberTryingToJoinOwnAccountError().message) {
                throw new Errors_1.MemberTryingToJoinOwnAccountError(memberCreateRequest);
            }
            throw e;
        });
        this.logger.info('Create member done', { response: costaCreateResponse.data });
    }
    async updateMember(memberId, memberUpdateRequest) {
        this.logger.info('Calling costa with member update request', { memberUpdateRequest });
        const costaCreateResponse = await this.costaClient.put(`/api/members/${memberId}`, memberUpdateRequest);
        this.logger.info('Update member done', { response: costaCreateResponse.data });
    }
    async getMemberByPhoneNumber(phoneNumber) {
        const response = await this.costaClient.get(`/api/members/phoneNumber/${phoneNumber}`);
        return response.data;
    }
    async getMemberByOidc(oidcSub) {
        const response = await this.costaClient.get(`/api/members/oidcSub/${oidcSub}`);
        return response.data;
    }
    async getMemberById(memberId) {
        const response = await this.costaClient.get(`/api/members/${memberId}`);
        return response.data;
    }
    async getAllMembers() {
        const response = await this.costaClient.get('/api/members');
        return response.data;
    }
    async getAllMembersByCustomerId(customerId) {
        const response = await this.costaClient.get(`/api/members/customerId/${customerId}`);
        return response.data;
    }
    async createRiseupAccount(createRiseupAccountRequest) {
        this.logger.info('Calling costa with create Riseup Account request', createRiseupAccountRequest);
        const response = await this.costaClient.post('/api/riseup-accounts', createRiseupAccountRequest);
        this.logger.info('Riseup Account created successfully', { response: response.data });
        return RiseupAccount_1.RiseupAccount.parse(response.data);
    }
    async getRiseupAccountByPhoneNumber(phoneNumber) {
        const { data } = await this.costaClient.get(`/api/riseup-accounts/phone-number/${phoneNumber}`);
        return RiseupAccount_1.RiseupAccount.nullable().parse(data);
    }
    async getRiseupAccountById(riseupAccountId) {
        const { data } = await this.costaClient.get(`/api/riseup-accounts/${riseupAccountId}`);
        return RiseupAccount_1.RiseupAccount.nullable().parse(data);
    }
    async getProductsByPhoneNumber(phoneNumber) {
        const { data } = await this.costaClient.get(`/api/riseup-accounts/${phoneNumber}/products`);
        return RiseupAccount_1.ProductsWithAccountId.nullable().parse(data);
    }
    async getRiseupAccountsByMarket(market) {
        const response = await this.costaClient.get(`/api/riseup-accounts/by-market/${market}`);
        return response.data;
    }
    async getRiseupAccountIdsByMarket(market) {
        const response = await this.costaClient.get(`/api/riseup-accounts/id/by-market/${market}`);
        return response.data;
    }
    async getMarketByRiseupAccountId(riseupAccountId) {
        const response = await this.costaClient.get(`/api/riseup-accounts/${riseupAccountId}/market`);
        return RiseupAccount_1.RiseupMarket.parse(response.data);
    }
    async _updateProductsStatusOnRiseupAccount(riseupAccountId, riseupAccountProducts, endpointSuffix) {
        this.logger.info(`Calling costa [${endpointSuffix}] with update product on Riseup Account request`, { riseupAccountId, riseupAccountProducts });
        const response = await this.costaClient.put(`/api/riseup-accounts/${RiseupAccount_1.RiseupAccountId.parse(riseupAccountId)}/${endpointSuffix}`, RiseupAccountRequests_1.ChangeProductStatusRequest.parse(riseupAccountProducts));
        this.logger.info('Products updated successfully', { response: response.data });
        return RiseupAccount_1.RiseupAccount.parse(response.data);
    }
    async enableProductsOnRiseupAccount(riseupAccountId, riseupAccountProducts) {
        return await this._updateProductsStatusOnRiseupAccount(riseupAccountId, riseupAccountProducts, 'enable-products');
    }
    async disableProductsOnRiseupAccount(riseupAccountId, riseupAccountProducts) {
        return await this._updateProductsStatusOnRiseupAccount(riseupAccountId, riseupAccountProducts, 'disable-products');
    }
    async signup(signupRequest) {
        const response = await this.costaClient.post('/api/riseup-accounts/signup', signupRequest).catch(e => {
            if (e.response.data === new Errors_1.MemberExistsError().message) {
                throw new Errors_1.MemberExistsError(signupRequest.createMemberRequest.phoneNumber);
            }
            throw e;
        });
        const { riseupAccountId } = response.data;
        return RiseupAccount_1.RiseupAccountId.parse(riseupAccountId);
    }
    async isCashflower(phoneNumber) {
        const response = await this.costaClient.get(`/api/members/${phoneNumber}/has-product/${RiseupAccount_1.RiseupProduct.enum.cashflow}`);
        return response.data;
    }
    async isSaver(phoneNumber) {
        const response = await this.costaClient.get(`/api/members/${phoneNumber}/has-product/${RiseupAccount_1.RiseupProduct.enum.saving}`);
        return response.data;
    }
    async isChallenger(phoneNumber) {
        const response = await this.costaClient.get(`/api/members/${phoneNumber}/has-product/${RiseupAccount_1.RiseupProduct.enum.challenges}`);
        return response.data;
    }
    async resetPasswordInAuth0(memberId) {
        await this.costaClient.put(`/api/members/reset-password-in-auth0/${memberId}`);
    }
    async makeCustomerRequire2fa(customerId) {
        await this.costaClient.post(`/api/members/requireCustomerFor2fa/${customerId}`);
    }
    async isCustomerRequired2fa(customerId) {
        const { data } = await this.costaClient.get(`/api/members/requireCustomerFor2fa/${customerId}`);
        return data;
    }
    async getTrackingCategorySettings(customerId) {
        const { data } = await this.costaClient.get(`/api/tracking-categories/${customerId}`);
        return data;
    }
    async deleteProduct(riseupAccountId, product) {
        await this.costaClient.delete(`/api/immediate-sweep/${riseupAccountId}/${product}`);
    }
}
exports.CostaClient = CostaClient;
//# sourceMappingURL=api.js.map